import { AfterViewInit, Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective, NzInputGroupComponent, NzInputGroupWhitSuffixOrPrefixDirective } from 'ng-zorro-antd/input';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT, WIDTH_VIEW_LIST_COMPONENT_ACTIONS } from 'src/app/core/constants/components';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { IconDefinition } from '@ant-design/icons-angular';
import { PlusOutline } from '@ant-design/icons-angular/icons';
import _ from 'lodash';
import { NzModalService } from 'ng-zorro-antd/modal';
import { showDialog } from 'src/app/shared/utils/utils';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LossReasonDto } from 'src/app/shared/dto/loss-reason.dto';
import { LossReasonsResponse, LossReasonsService } from '../../services/loss-reasons.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

const icons: IconDefinition[] = [
    PlusOutline
];
@Component({
    selector: 'app-loss-reasons',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        NzIconModule,
        NzInputGroupComponent,
        NzInputGroupWhitSuffixOrPrefixDirective,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './loss-reasons.component.html',
    styleUrl: './loss-reasons.component.scss'
})

export class LossReasonsComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    totalLossReasons: number = 0;
    pageSize: number = 10;
    pageIndex: number = 1;
    filterText: string = '';

    fieldsReadOnly: boolean = true;
    drawerTitle: string = '';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    lossReasonSelected: LossReasonDto | null = null;
    lossReasons: LossReasonDto[] = [];
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;

    tableHeight!: number;
    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;
    WIDTH_VIEW_LIST_COMPONENT_ACTIONS = WIDTH_VIEW_LIST_COMPONENT_ACTIONS;

    validateForm: FormGroup<{
        name: FormControl<string>;
    }>;

    @ViewChild('tableContainer') private readonly _tableContainer!: ElementRef;

    private readonly lossReasonsService = inject(LossReasonsService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly iconService = inject(NzIconService);
    private readonly modalService = inject(NzModalService);

    private unsubscribe$ = new Subject<void>();
    private unsubscribeById$ = new Subject<void>();

    constructor() {
        super('lossReasons', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.iconService.addIcon(...icons);
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.lossReasonsService.setupWebSocketPaginated(this.pageSize, this.pageIndex);
        this.lossReasonsService.lossReasonsPaginated$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (response: LossReasonsResponse) => {
                    this.lossReasons = _.cloneDeep(response.lossReasons);
                },
                error: () => {
                    this.message.error('Error cargando razones de merma');
                }
            });

        this.isLoading = false;
    }

    async ngAfterViewInit(): Promise<void> {
        this.innerWidth = window.innerWidth;
        setTimeout(() => {
            this.tableHeight =
                (this._tableContainer.nativeElement as HTMLImageElement)
                    .clientHeight - TITLE_TABLE_SIZE; // X depend of your page display
        });
    }

    ngOnDestroy(): void {
        this.lossReasonsService.disconnectWebSocketPaginated();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        if (this.lossReasonSelected) {
            this.lossReasonsService.disconnectWebSocketById(this.lossReasonSelected.id);
            this.unsubscribeById$.next();
            this.unsubscribeById$.complete();
        }

    }

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        const { pageSize, pageIndex, sort, filter } = params;

        this.pageSize = pageSize;
        this.pageIndex = pageIndex;

        await this.getPaginatedLossReasons();
    }

    async clearFilter(): Promise<void> {
        this.filterText = '';
        this.pageIndex = 1;

        await this.getPaginatedLossReasons();
    }

    async getPaginatedLossReasons() {
        this.isLoading = true;
        const response = await this.lossReasonsService.getPaginatedLossReasons(this.pageSize, this.pageIndex, this.filterText);

        this.totalLossReasons = response.total;
        this.pageIndex = response.page ?? this.pageIndex;
        this.lossReasons = response.lossReasons;
        this.lossReasonsService.refreshVariables(this.pageSize, this.pageIndex, this.filterText);
        this.isLoading = false;
    }

    create() {
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nueva razón de merma';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    async view(data: LossReasonDto) {
        await this.lossReasonsService.setupWebSocketById(data.id);
        this.lossReasonsService.lossReasonById$
            .pipe(takeUntil(this.unsubscribeById$))
            .subscribe({
                next: (lossReason: LossReasonDto | null) => {
                    if (lossReason && lossReason.id) {
                        this.setData(lossReason);
                        this.message.info('Razón de merma actualizada por otro usuario');
                    }
                },
                error: () => {
                    this.message.error('Error actualizando información de razón de merma');
                }
            });

        this.setData(data);

        this.validateForm.disable();

        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo razón de merma ' + this.lossReasonSelected?.name;
        this.fieldsReadOnly = true;

        this.openDrawer();
    }

    async edit(data: LossReasonDto) {
        await this.lossReasonsService.setupWebSocketById(data.id);
        this.lossReasonsService.lossReasonById$
            .pipe(takeUntil(this.unsubscribeById$))
            .subscribe({
                next: async (lossReason: LossReasonDto | null) => {
                    if (lossReason && lossReason.id) {
                        if (lossReason.updatedById !== this.currentUser.id) {
                            if (await showDialog(this.modalService, 'Razón de merma actualizada por otro usuario, ¿deseas cargar los datos actualizados?')) {
                                this.setData(lossReason);
                                this.message.info('Formulario actualizado');
                            }
                        } else {
                            this.setData(lossReason);
                        }
                    }
                },
                error: () => {
                    this.message.error('Error actualizando información de razón de merma');
                }
            });

        this.setData(data);

        this.validateForm.enable();

        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando razón de merma ' + this.lossReasonSelected?.name;
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    async delete(id: number) {
        if (
            await showDialog(this.modalService, '¿Estás seguro de que quieres eliminar esta razón de merma? Esta acción no se puede deshacer.')
        ) {
            this.lossReasonsService.delete(id).subscribe({
                next: (lossReason: LossReasonDto) => {
                    try {
                        if (lossReason == undefined) {
                            this.message.create('error', `No se ha podido eliminar la razón de merma, razón de merma no encontrada`);

                            return;
                        }

                        const index = this.lossReasons.findIndex((p) => p.id === id);

                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar la razón ${lossReason.name} con id ${id}, razón de merma no encontrada`);

                            return;
                        }

                        this.lossReasons.splice(index, 1);
                        const data = [...this.lossReasons];
                        this.lossReasons = data;

                        this.message.create('success', 'Razón de merma eliminada correctamente');
                    } catch (error: any) {
                        this.message.create('error', `Error eliminando razón de merma. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.message.create('error', `Error eliminando razón de merma, error de servidor. Error: ${error.message}`);
                },
            });
        }
    }

    submitForm() {
        this.isSaving = true;
        const lossReasonDto: LossReasonDto = {
            id: this.lossReasonSelected ? this.lossReasonSelected.id : 0,
            name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : ''
        };

        if (!this.validForm(lossReasonDto)) {
            this.isSaving = false;
            return;
        }

        if (this.drawerMode === DrawerMode.CREATE) {
            this.lossReasonsService.save(lossReasonDto).subscribe({
                next: (lossReason: LossReasonDto) => {
                    try {
                        if (lossReason == undefined) {
                            throw new Error('Error creating loss reason');
                        }

                        this.message.create('success', 'Razón de merma creada correctamente');
                        this.isSaving = false;

                        this.closeDrawer();
                    } catch (error: any) {
                        this.isSaving = false;
                        this.message.create('error', `Error creando razón de merma. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.isSaving = false;
                    this.message.create('error', `Error creando razón de merma, error de servidor. Error: ${error.message}`);
                }
            });
        } else if (this.drawerMode === DrawerMode.EDIT) {
            this.lossReasonsService.update(lossReasonDto.id, lossReasonDto).subscribe({
                next: (lossReason: LossReasonDto) => {
                    try {
                        if (lossReason == undefined) {
                            this.isSaving = false;
                            this.message.create('error', 'Error actualizando la razón de merma');

                            return;
                        }

                        const index = this.lossReasons.findIndex(
                            (p) => p.id === lossReason.id,
                        );

                        if (index < 0) {
                            this.isSaving = false;
                            this.message.create('error', `No se ha podido actualizar la razón ${lossReasonDto.name} con id ${lossReasonDto.id}, razón de merma no encontrada`);

                            return;
                        }

                        this.lossReasons[index] = lossReason;
                        const data = [...this.lossReasons];
                        this.lossReasons = data;
                        this.isSaving = false;

                        this.message.create('success', 'Razón de merma actualizada correctamente');

                        this.closeDrawer();
                    } catch (error: any) {
                        this.isSaving = false;
                        this.message.create('error', `Error actualizando razón de merma. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.isSaving = false;
                    this.message.create('error', `Error actualizando razón de merma, error de servidor. Error: ${error.message}`);
                }
            });
        }
    }

    closeDrawer() {
        if (this.drawerMode === DrawerMode.VIEW || this.drawerMode === DrawerMode.EDIT) {
            this.lossReasonsService.disconnectWebSocketById(this.lossReasonSelected!.id);
            this.unsubscribeById$.next();
            this.unsubscribeById$.complete();
        }
        this.drawerMode = DrawerMode.NONE;
        this.drawerVisible = false;
        this.lossReasonSelected = null;
        this.validateForm.reset();
    }

    private openDrawer() {
        this.drawerVisible = true;
    }

    private validForm(cause: LossReasonDto): boolean {
        if (!cause.name) {
            this.message.create('error', 'El nombre del razón de la merma es obligatorio');
            return false;
        }
        return true;
    }

    private setData(data: LossReasonDto) {
        this.lossReasonSelected = data;
        this.validateForm.patchValue(data);
    }
}
